"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { HiOutlineChevronDown, HiOutlineDocumentText } from "react-icons/hi";
import { RiSearchLine } from "react-icons/ri";
import { Button, Input } from "@/components/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { useDebounce } from "@/hooks/use-debounce";
import type { EmailTemplate } from "@/types/email-template";
import { TemplatePreview } from "./template-preview";
import { useEmailTemplatesInfinite } from "@/query/email-template";
interface LoadTemplateMenuProps {
  onSelect: (template: EmailTemplate) => void;
  disabled?: boolean;
}

/**
 * Picker that loads a saved template into a composer. Shows the same previews as
 * the templates settings page — you pick an email by recognizing it, not by
 * recalling what its name meant.
 *
 * Paging a dropdown mid-compose would be miserable, so the list grows as it
 * scrolls and search narrows it server-side. Both matter: every row carries a
 * full body, and this menu mounts on five compose surfaces.
 */
export function LoadTemplateMenu(t0) {
  const $ = _c(28);
  const {
    onSelect,
    disabled
  } = t0;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  let t1;
  if ($[0] !== debouncedSearch) {
    t1 = debouncedSearch.trim() || undefined;
    $[0] = debouncedSearch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      search: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    templates,
    isLoading,
    hasNextPage,
    fetchNextPage
  } = useEmailTemplatesInfinite(t2);
  const [loaderNode, setLoaderNode] = useState(null);
  let t3;
  let t4;
  if ($[4] !== fetchNextPage || $[5] !== hasNextPage || $[6] !== loaderNode) {
    t3 = () => {
      if (!loaderNode || !hasNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      }, {
        rootMargin: "200px"
      });
      observer.observe(loaderNode);
      return () => observer.disconnect();
    };
    t4 = [loaderNode, fetchNextPage, hasNextPage];
    $[4] = fetchNextPage;
    $[5] = hasNextPage;
    $[6] = loaderNode;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  const hasSearch = debouncedSearch.trim().length > 0;
  if (templates.length === 0 && !hasSearch) {
    return null;
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <HiOutlineDocumentText />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <HiOutlineChevronDown className="ml-1 size-3" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== disabled) {
    t7 = <DropdownMenuTrigger asChild={true}><Button type="button" variant="ghost" size="sm" disabled={disabled} icon={t5} className="shrink-0 text-caption text-foreground-muted">Select template{t6}</Button></DropdownMenuTrigger>;
    $[11] = disabled;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DropdownMenuLabel>Email templates</DropdownMenuLabel>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e => setSearch(e.target.value);
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== search) {
    t10 = <div className="px-2 pb-1"><Input type="text" value={search} onChange={t9} icon={RiSearchLine} placeholder="Search templates..." /></div>;
    $[15] = search;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== hasNextPage || $[18] !== isLoading || $[19] !== onSelect || $[20] !== templates) {
    t11 = isLoading ? <p className="px-2 py-6 text-center text-caption text-foreground-muted">Loading templates...</p> : templates.length === 0 ? <p className="px-2 py-6 text-center text-caption text-foreground-muted">No templates match your search.</p> : <><div className="flex flex-wrap gap-3 p-2">{templates.map(template => <DropdownMenuItem key={template.id} onSelect={() => onSelect(template)} className="group flex w-40 flex-col items-stretch overflow-hidden rounded-lg border border-border p-0 text-left transition-colors hover:border-border-strong hover:bg-hover"><TemplatePreview template={template} className="h-24 border-b border-border" /><span className="truncate px-2 pb-0.5 pt-1.5 font-medium text-caption text-foreground">{template.name}</span><span className="truncate px-2 pb-1.5 text-caption text-foreground-muted">{template.subject || "No subject"}</span></DropdownMenuItem>)}</div>{hasNextPage && <div ref={setLoaderNode} className="h-4" />}</>;
    $[17] = hasNextPage;
    $[18] = isLoading;
    $[19] = onSelect;
    $[20] = templates;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <DropdownMenuContent align="start" className="max-h-96 w-[34rem] overflow-y-auto">{t8}{t10}{t11}</DropdownMenuContent>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t7) {
    t13 = <DropdownMenu>{t7}{t12}</DropdownMenu>;
    $[25] = t12;
    $[26] = t7;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}